import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import Switch from "react-switch";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAssetCategories, getAstInstitutions } from "../../services/ValitanApiService";
import { exportGamisTemplate, exportGamisTemplateZipped } from "../../services/PricingService";
import { getNamingConfigSummary } from "../../services/BarcodeService";

const DEFAULT_CATEGORY = "ALL";

const parseListPayload = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getCategoryCode = (category) =>
  String(category?.code ?? category?.category_code ?? category?.categoryCode ?? "").trim().toUpperCase();

const getCategoryName = (category) =>
  String(category?.name ?? category?.category_name ?? category?.categoryName ?? getCategoryCode(category)).trim();

const getInstitutionId = (institution) =>
  String(institution?.id ?? institution?.institution_id ?? institution?.institutionId ?? "").trim();

const getInstitutionName = (institution) =>
  String(institution?.name ?? institution?.institution_name ?? institution?.institutionName ?? "").trim();

const getInstitutionWardName = (institution) =>
  String(institution?.wardName ?? institution?.ward_name ?? institution?.ward?.name ?? "").trim();

const getNamingConfigCategoryCode = (config) =>
  String(config?.asset_category_code ?? config?.assetCategoryCode ?? "").trim().toUpperCase();

const getNamingConfigInstitutionFilters = (config) => {
  const rawFilters = Array.isArray(config?.institution_filters)
    ? config.institution_filters
    : Array.isArray(config?.institutionFilters)
      ? config.institutionFilters
      : [];

  if (rawFilters.length > 0) {
    return rawFilters
      .map((filter) => ({
        name: String(filter?.name || "").trim(),
        wardId: String(filter?.ward_id || filter?.wardId || "").trim(),
        wardName: String(filter?.ward_name || filter?.wardName || "").trim(),
      }))
      .filter((filter) => filter.name);
  }

  const fallbackName = String(config?.institution_name ?? config?.institutionName ?? "").trim();
  const fallbackWardId = String(config?.ward_id ?? config?.wardId ?? "").trim();
  const fallbackWardName = String(config?.ward_name ?? config?.wardName ?? "").trim();
  if (!fallbackName) return [];
  return [{ name: fallbackName, wardId: fallbackWardId, wardName: fallbackWardName }];
};

const getNamingConfigDisplayName = (config) => {
  const filters = getNamingConfigInstitutionFilters(config);
  if (filters.length === 0) {
    return String(config?.institution_name ?? config?.institutionName ?? "").trim();
  }

  return filters
    .map((filter) => formatInstitutionWardName(filter.name, filter.wardName))
    .join(", ");
};

const getInstitutionMatchKey = (name, wardName = "") =>
  `${String(name || "").trim().toLowerCase()}|${String(wardName || "").trim().toLowerCase()}`;

const formatInstitutionWardName = (name, wardName) => {
  const normalizedName = String(name || "").trim();
  const normalizedWard = String(wardName || "").trim();
  if (!normalizedWard || normalizedWard === "Unknown") return normalizedName;
  return `${normalizedName}-${normalizedWard}`;
};

const safeFilenamePart = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

const downloadBlobResponse = (response, fallbackName) => {
  const blob = response?.data;
  if (!blob || blob.size === 0) {
    throw new Error("The generated GAMIS export file was empty.");
  }

  const contentDisposition = response?.headers?.["content-disposition"] || "";
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = decodeURIComponent(filenameMatch?.[1] || fallbackName);

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

const GamisExports = () => {
  document.title = "GAMIS exports | Valitan";

  const [categories, setCategories] = useState([]);
  const [selectedCategoryCode, setSelectedCategoryCode] = useState(DEFAULT_CATEGORY);
  const [maxRows, setMaxRows] = useState("");
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [namingConfigs, setNamingConfigs] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [loadingNamingConfigs, setLoadingNamingConfigs] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingZipped, setGeneratingZipped] = useState(false);
  const [useConfiguredNames, setUseConfiguredNames] = useState(false);
  const [condition, setCondition] = useState("");

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await getAssetCategories(0, 1000);
        const list = parseListPayload(response)
          .map((category) => ({
            value: getCategoryCode(category),
            label: `${getCategoryCode(category)} - ${getCategoryName(category)}`,
          }))
          .filter((category) => category.value);

        if (isMounted) {
          list.unshift({ value: "ALL", label: "ALL - All Asset Categories" });
          setCategories(list);
          if (!list.some((category) => category.value === DEFAULT_CATEGORY) && list.length > 0) {
            setSelectedCategoryCode(list[0].value);
          }
        }
      } catch (error) {
        if (isMounted) {
          enqueueSnackbar("Failed to load asset categories.", { variant: "error" });
        }
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    };

    loadCategories();

    setLoadingInstitutions(true);
    getAstInstitutions(0, 2000)
      .then((res) => {
        const list = parseListPayload(res)
          .map((i, index) => {
            const id = getInstitutionId(i);
            const name = getInstitutionName(i);
            const wardName = getInstitutionWardName(i);
            const labelText = name + (wardName && wardName !== "Unknown" ? ` - Ward: ${wardName}` : "");
            return {
              value: id || `${name || "institution"}-${wardName || "unknown"}-${index}`,
              label: labelText,
              id,
              name,
              wardName,
            };
          })
          .filter((i) => i.name)
          .sort((a, b) => a.label.localeCompare(b.label));
        if (isMounted) setInstitutions(list);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoadingInstitutions(false); });

    setLoadingNamingConfigs(true);
    getNamingConfigSummary()
      .then((res) => {
        const list = parseListPayload(res)
          .map((config, index) => {
            const categoryCode = getNamingConfigCategoryCode(config);
            const institutionShortName = String(config.institution_short_name || config.institutionShortName || "").trim();
            const catLabel = categoryCode ? `[${categoryCode}]` : "[All]";
            const instShort = institutionShortName ? ` -> ${institutionShortName}` : "";
            return {
              value: String(config.id || `config-${categoryCode || "all"}-${index}`),
              label: `${getNamingConfigDisplayName(config)} ${catLabel}${instShort}`,
              id: "",
              name: getNamingConfigDisplayName(config),
              categoryCode,
              config,
            };
          })
          .filter((config) => config.name)
          .sort((a, b) => a.label.localeCompare(b.label));
        if (isMounted) setNamingConfigs(list);
      })
      .catch(() => {
        if (isMounted) {
          setNamingConfigs([]);
          enqueueSnackbar("Failed to load barcode naming configurations.", { variant: "error" });
        }
      })
      .finally(() => { if (isMounted) setLoadingNamingConfigs(false); });

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.value === selectedCategoryCode) || null,
    [categories, selectedCategoryCode]
  );

  const institutionOptions = useMemo(() => {
    if (!useConfiguredNames) return institutions;

    return namingConfigs.filter((option) =>
      !option.categoryCode ||
      option.categoryCode === "ALL" ||
      selectedCategoryCode === DEFAULT_CATEGORY ||
      option.categoryCode === selectedCategoryCode
    );
  }, [institutions, namingConfigs, selectedCategoryCode, useConfiguredNames]);

  const institutionLookupByNameWard = useMemo(
    () =>
      new Map(
        institutions.map((institution) => [
          getInstitutionMatchKey(institution.name, institution.wardName),
          institution,
        ])
      ),
    [institutions]
  );

  const institutionLookupByName = useMemo(
    () =>
      institutions.reduce((acc, institution) => {
        const key = String(institution.name || "").trim().toLowerCase();
        if (!key) return acc;
        const existing = acc.get(key) || [];
        existing.push(institution);
        acc.set(key, existing);
        return acc;
      }, new Map()),
    [institutions]
  );

  const resolveSelectedConfiguredInstitutionIds = useMemo(() => {
    if (!useConfiguredNames || selectedInstitutions.length === 0) return [];

    const matchedIds = new Set();
    selectedInstitutions.forEach((option) => {
      getNamingConfigInstitutionFilters(option.config).forEach((filter) => {
        const exactMatch = institutionLookupByNameWard.get(
          getInstitutionMatchKey(filter.name, filter.wardName)
        );
        if (exactMatch?.id) {
          matchedIds.add(String(exactMatch.id));
          return;
        }

        const fallbackMatches = institutionLookupByName.get(
          String(filter.name || "").trim().toLowerCase()
        ) || [];
        fallbackMatches.forEach((institution) => {
          if (institution?.id) matchedIds.add(String(institution.id));
        });
      });
    });

    return Array.from(matchedIds);
  }, [
    institutionLookupByName,
    institutionLookupByNameWard,
    selectedInstitutions,
    useConfiguredNames,
  ]);

  const handleConfiguredNamesToggle = (checked) => {
    setUseConfiguredNames(checked);
    setSelectedInstitutions([]);
  };

  const handleGenerate = async (event) => {
    event.preventDefault();

    const categoryCode = String(selectedCategoryCode || "").trim().toUpperCase();
    const trimmedMaxRows = String(maxRows || "").trim();
    const rowLimit = trimmedMaxRows ? Number(trimmedMaxRows) : null;
    const selectedInstitutionIds = useConfiguredNames
      ? resolveSelectedConfiguredInstitutionIds
      : selectedInstitutions.map((institution) => institution.id).filter(Boolean);
    const selectedInstitutionNames = useConfiguredNames
      ? Array.from(
          new Set(
            selectedInstitutions.flatMap((option) =>
              getNamingConfigInstitutionFilters(option.config).map((filter) => filter.name).filter(Boolean)
            )
          )
        )
      : selectedInstitutions.map((institution) => institution.name).filter(Boolean);
    const institutionSearch = selectedInstitutionNames.join(",");

    if (!categoryCode) {
      enqueueSnackbar("Select an asset category.", { variant: "warning" });
      return;
    }
    if (rowLimit !== null && (!Number.isInteger(rowLimit) || rowLimit < 1)) {
      enqueueSnackbar("Enter a valid max rows value greater than zero.", { variant: "warning" });
      return;
    }

    setGenerating(true);
    try {
      const categoryPart = safeFilenamePart(selectedCategory?.label || categoryCode) || categoryCode.toLowerCase();
      const institutionPart = safeFilenamePart(institutionSearch) || "all_institutions";
      const response = await exportGamisTemplate({
        assetCategoryCode: categoryCode,
        maxRows: rowLimit || undefined,
        institutionSearch: institutionSearch.trim() || undefined,
        institutionIds: selectedInstitutionIds.length > 0 ? selectedInstitutionIds : undefined,
        useConfiguredNames: useConfiguredNames || undefined,
        condition: String(condition || "").trim() || undefined,
      });
      downloadBlobResponse(
        response,
        `gamis_${categoryPart}_${institutionPart}_${rowLimit || "all"}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      enqueueSnackbar("GAMIS template generated successfully.", { variant: "success" });
    } catch (error) {
      const status = error?.response?.status;
      const message =
        status === 400
          ? "This asset category is not configured for GAMIS export."
          : "Failed to generate GAMIS template. Please try again.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateZipped = async (event) => {
    event.preventDefault();

    const categoryCode = String(selectedCategoryCode || "").trim().toUpperCase();
    const trimmedMaxRows = String(maxRows || "").trim();
    const rowLimit = trimmedMaxRows ? Number(trimmedMaxRows) : null;
    const selectedInstitutionIds = useConfiguredNames
      ? resolveSelectedConfiguredInstitutionIds
      : selectedInstitutions.map((institution) => institution.id).filter(Boolean);
    const selectedInstitutionNames = useConfiguredNames
      ? Array.from(
          new Set(
            selectedInstitutions.flatMap((option) =>
              getNamingConfigInstitutionFilters(option.config).map((filter) => filter.name).filter(Boolean)
            )
          )
        )
      : selectedInstitutions.map((institution) => institution.name).filter(Boolean);
    const institutionSearch = selectedInstitutionNames.join(",");

    if (!categoryCode) {
      enqueueSnackbar("Select an asset category.", { variant: "warning" });
      return;
    }

    setGeneratingZipped(true);
    try {
      const response = await exportGamisTemplateZipped({
        assetCategoryCode: categoryCode,
        maxRows: rowLimit || undefined,
        institutionSearch: institutionSearch.trim() || undefined,
        institutionIds: selectedInstitutionIds.length > 0 ? selectedInstitutionIds : undefined,
        useConfiguredNames: useConfiguredNames || undefined,
        condition: String(condition || "").trim() || undefined,
      });
      downloadBlobResponse(
        response,
        `gamis_batches_${categoryCode.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.zip`
      );
      enqueueSnackbar("Zipped GAMIS batches generated successfully.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to generate zipped GAMIS batches.", { variant: "error" });
    } finally {
      setGeneratingZipped(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="GAMIS exports" />

        <Row>
          <Col lg={8}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div>
                    <h4 className="card-title mb-1">Generate GAMIS Template</h4>
                    <p className="text-muted mb-0">
                      Download a GAMIS-ready Excel template using the backend export mapping.
                    </p>
                  </div>
                  <span className="badge bg-primary-subtle text-primary">Excel .xlsx</span>
                </div>
              </CardHeader>
              <CardBody>
                <Alert color="info" className="mb-4">
                  The export fills department, vote, institution, class, description, and financial year fields on the fly.
                  It does not update asset department IDs in the database.
                </Alert>

                <Form onSubmit={handleGenerate}>
                  <Row className="g-3">
                    <Col md={7}>
                      <Label className="form-label">Asset Category</Label>
                      <Select
                        isLoading={loadingCategories}
                        options={categories}
                        value={selectedCategory}
                        onChange={(option) => setSelectedCategoryCode(option?.value || "")}
                        placeholder="Select category"
                        isClearable={false}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </Col>
                    <Col md={5}>
                      <Label className="form-label">Max Rows</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={maxRows}
                        onChange={(event) => setMaxRows(event.target.value)}
                        placeholder="Leave blank to export all"
                      />
                      <small className="text-muted">Optional. Leave blank to export all matching records.</small>
                    </Col>
                    <Col md={7}>
                      <Label className="form-label">Condition (Optional)</Label>
                      <Select
                        options={[
                          { value: "Good", label: "Good" },
                          { value: "Fair", label: "Fair" },
                          { value: "Poor", label: "Poor" },
                          { value: "Very poor", label: "Very poor" }
                        ]}
                        value={condition ? { value: condition, label: condition } : null}
                        onChange={(selected) => setCondition(selected ? selected.value : "")}
                        placeholder="Filter by condition..."
                        isClearable
                      />
                      <small className="text-muted">Optional. Export only assets matching this condition.</small>
                    </Col>
                    <Col md={12}>
                      <Label className="form-label">
                        {useConfiguredNames ? "Naming Configuration Institution (Optional)" : "Institution (Optional)"}
                      </Label>
                      <Select
                        isLoading={useConfiguredNames ? loadingNamingConfigs : loadingInstitutions}
                        options={institutionOptions}
                        value={selectedInstitutions}
                        onChange={(selectedOptions) => setSelectedInstitutions(selectedOptions || [])}
                        placeholder={useConfiguredNames ? "All configured institutions" : "All institutions"}
                        isClearable
                        isMulti
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      <small className="text-muted d-block mb-3">
                        {useConfiguredNames
                          ? "Leave blank to export configured names for all institutions, or select institutions to export only their related assets."
                          : "Leave blank to export from all institutions."}
                      </small>
                    </Col>
                    <Col md={12} className="mb-2">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Switch
                          id="useConfiguredNamesToggle"
                          checked={useConfiguredNames}
                          onChange={handleConfiguredNamesToggle}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          onColor="#4b38b3"
                          offColor="#adb5bd"
                          onHandleColor="#ffffff"
                          offHandleColor="#ffffff"
                          height={20}
                          width={42}
                        />
                        <Label htmlFor="useConfiguredNamesToggle" className="mb-0 fw-bold text-dark" style={{ cursor: "pointer", userSelect: "none" }}>
                          Use Curated Configured Names
                        </Label>
                      </div>
                      <small className="text-muted d-block">
                        When checked, the exporter will use custom curated institution names and location short codes mapped in the Barcode Naming Configuration instead of default values.
                      </small>
                    </Col>
                    <Col xs={12} className="d-flex flex-wrap gap-2">
                      <Button color="primary" type="submit" disabled={generating || generatingZipped || loadingCategories}>
                        {generating ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <i className="ri-file-excel-2-line me-2" />
                            Generate GAMIS Excel
                          </>
                        )}
                      </Button>
                      <Button color="info" outline onClick={handleGenerateZipped} disabled={generating || generatingZipped || loadingCategories}>
                        {generatingZipped ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Generating Zip...
                          </>
                        ) : (
                          <>
                            <i className="ri-folder-zip-line me-2" />
                            Generate Zipped Batches (500/file)
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Current Logic</h5>
              </CardHeader>
              <CardBody>
                <ul className="ps-3 mb-0 text-muted">
                  <li>FURN class is exported as Furniture.</li>
                  <li>Description uses ASSET NAME - Description.</li>
                  <li>Financial year comes from acquisition year.</li>
                  <li>Department is matched from the GAMIS template data.</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GamisExports;
